import { useEffect } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

import Wrapper from 'components/layouts/Wrapper/Wrapper';
import ListEmpty from 'components/modules/Common/ListEmpty/ListEmpty';
import { WeeklyPayouts } from '../WeeklyPayouts/WeeklyPayouts';
import {
  BalanceStatsBar,
  LoadingSpinner,
  ToolBar,
  Typhography,
  Headers,
  SelectBox,
} from 'components/core';

import { balanceWeekTabnavData } from 'constants/TabNavData';
import { upcomingDropdownItemsWeek } from 'constants/DateRangeDropDownDatas';
import { addDaysFunction, getCurrentWeekStartandEndDates } from 'shared/utilities/dataConversion';

import { getURLParams, useDateRangeSorting } from 'hooks/useDateRangeSorting';
import { useCurrencyFormat } from 'hooks/useCurrencyFormat';

import {
  useGetPendingBalanceApiQuery,
  useGetUpcomingBalanceHeroStatsQuery,
} from '../../../../../services/pastBalanceApi';

const UpcomingWeekContainer = () => {
  const [
    rangeSelector,
    defaultSortingText,
    itemSlectedState,
    showDropdown,
    handlerSortingDropDownClick,
    handlerSortingOption,
  ]: any = useDateRangeSorting('All', upcomingDropdownItemsWeek);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    navigate(
      {
        pathname: location.pathname,
        search: `?${createSearchParams(getURLParams(defaultSortingText))}`,
      },
      { replace: true }
    );
  }, [defaultSortingText]);

  const [wkStart] = getCurrentWeekStartandEndDates(
    moment().tz('America/Chicago').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format()
  );

  const setLessThanDate = moment(wkStart)
    .tz('America/Chicago')
    .add(7 + rangeSelector, 'day')
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .format();
  const setGreaterThan = moment(wkStart)
    .tz('America/Chicago')
    .add(7, 'day')
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .format();

  /*
   * List the Upcoming Invoices
   */
  const upcomingInvoiceParams = {
    first: 3000,
    offset: 0,
    filter: {
      status: { in: ['INVOICED', 'APPROVED', 'PAID'] },
      customer_payment_due_at: {
        greaterThan: setGreaterThan,
        lessThan: setLessThanDate,
      },
    },
    groupBy: 'CUSTOMER_PAYMENT_DUE_AT_TRUNCATED_TO_WEEK_CST',
  };

  const { data, isLoading, isFetching, isSuccess } =
    useGetPendingBalanceApiQuery(upcomingInvoiceParams);
  const upcomingListData = isSuccess && data?.payouts?.groupedAggregates;

  /*
   * Fetch the Upcoming Invoices Hero Stats data
   */
  const upcomingHeroStatsParams = {
    filter: {
      status: { in: ['APPROVED', 'PAID', 'INVOICED'] },
      customer_payment_due_at: {
        greaterThan: setGreaterThan,
        lessThan: setLessThanDate,
      },
    },
    timeCycleDays: '' + rangeSelector + '',
  };

  const { data: upcomingBalanceStatsBarData, isSuccess: isSuccessUpcomingBalanceStatsBar }: any =
    useGetUpcomingBalanceHeroStatsQuery(upcomingHeroStatsParams);

  return (
    <>
      {(isLoading || isFetching) && <LoadingSpinner />}
      <Wrapper additionalClasses="flex-col items-start" pageTitle="Balance">
        <Headers headingContent="Balance" />
        {!isLoading && (
          <>
            <ToolBar navigationData={balanceWeekTabnavData} isShowOptionEnabled={false}>
              <SelectBox
                items={upcomingDropdownItemsWeek}
                itemClickHandler={handlerSortingOption}
                defaultSelectBoxText={defaultSortingText}
                itemSelected={itemSlectedState}
                dropDownState={showDropdown}
                dropDownClickHandler={handlerSortingDropDownClick}
              />
            </ToolBar>

            {isSuccessUpcomingBalanceStatsBar && (
              <BalanceStatsBar
                defaultSortingText={defaultSortingText}
                data={upcomingBalanceStatsBarData}
                timeFrame="upcoming"
              />
            )}

            {upcomingListData?.length > 0 && (
              <div className="flex flex-col w-full">
                <Typhography
                  component="div"
                  type="h2"
                  color="text-grey-900"
                  additionalClasses="items-center uppercase pt-9 pb-3 border-t border-solid border-grey-100 px-4 !font-normal"
                >
                  {'Invoices List'}
                </Typhography>

                <div className="flex flex-col w-full invoices-list-wrapper">
                  {upcomingListData.map((listItem: any) => {
                    const convertedPrice = parseFloat(listItem?.sum?.amount);

                    const setDate = moment(listItem.keys[0]).tz('America/Chicago');
                    const endOfWeek = addDaysFunction(setDate, 6);

                    return (
                      <div className="flex flex-col w-full mb-0" key={listItem.keys[0]}>
                        <WeeklyPayouts
                          weekStartDate={listItem.keys[0]}
                          weekEndDate={endOfWeek}
                          mainLabel="Invoice for"
                          subLabel={`${moment(listItem.keys[0])
                            .tz('America/Chicago')
                            .format('MM/DD/YYYY')} - ${moment(endOfWeek)
                            .tz('America/Chicago')
                            .format('MM/DD/YYYY')}`}
                          rightColContent={useCurrencyFormat(convertedPrice)}
                          isRemoveTopBorder={upcomingListData?.length === 1}
                          timeFrame={'upcoming'}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {upcomingListData?.length === 0 && <ListEmpty />}
          </>
        )}
      </Wrapper>
    </>
  );
};

export default UpcomingWeekContainer;
